import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import type { ProgramKerja } from '@/types/pocketbase'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import ProgressBar from '@/components/ui/ProgressBar'
import EmptyState from '@/components/ui/EmptyState'

export default function ProgramKerjaPage() {
  const [selectedDivisi, setSelectedDivisi] = useState('')

  const { data: divisiResult } = useQuery({
    queryKey: ['divisi'],
    queryFn: () => pb.collection('divisi').getList(1, 50, { sort: 'nama' }),
  })

  const { data: programResult, isLoading } = useQuery({
    queryKey: ['program-kerja', selectedDivisi],
    queryFn: () =>
      pb.collection('program_kerja').getList(1, 100, {
        filter: selectedDivisi ? `divisi_id = "${selectedDivisi}"` : '',
        expand: 'divisi_id',
        sort: 'nama',
      }),
  })

  const items = programResult?.items || []

  const statusVariant: Record<string, 'green' | 'yellow' | 'blue' | 'red'> = {
    completed: 'green',
    ongoing: 'blue',
    planned: 'yellow',
    cancelled: 'red',
  }

  const statusLabel: Record<string, string> = {
    completed: 'Selesai',
    ongoing: 'Berlangsung',
    planned: 'Direncanakan',
    cancelled: 'Dibatalkan',
  }

  return (
    <div className="py-4 space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setSelectedDivisi('')}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            selectedDivisi === ''
              ? 'bg-primary text-white'
              : 'bg-white text-text-secondary border border-gray-200'
          }`}
        >
          Semua
        </button>
        {divisiResult?.items.map((d) => (
          <button
            key={d.id}
            onClick={() => setSelectedDivisi(d.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              selectedDivisi === d.id
                ? 'bg-primary text-white'
                : 'bg-white text-text-secondary border border-gray-200'
            }`}
          >
            {d.nama}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-card h-28 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Belum ada program kerja"
          description="Program kerja akan ditampilkan di sini."
        />
      ) : (
        items.map((program) => {
          const capaian = program.capaian_tahun
            ? (() => {
                try {
                  return JSON.parse(program.capaian_tahun) as Record<string, number>
                } catch {
                  return {}
                }
              })()
            : {}

          return (
            <Card key={program.id}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-text-primary">{program.nama}</h3>
                  {(program.expand?.divisi_id as { nama?: string })?.nama && (
                    <p className="text-xs text-text-secondary">
                      {(program.expand?.divisi_id as { nama?: string }).nama}
                    </p>
                  )}
                </div>
                <Badge variant={statusVariant[program.status]}>
                  {statusLabel[program.status]}
                </Badge>
              </div>

              {program.target && (
                <p className="text-xs text-text-secondary mb-2">
                  <span className="font-medium">Target:</span> {program.target}
                </p>
              )}

              {Object.keys(capaian).length > 0 && (
                <div className="space-y-1">
                  {Object.entries(capaian).map(([tahun, pct]) => (
                    <div key={tahun} className="flex items-center gap-2">
                      <span className="text-xs text-text-secondary w-10">{tahun}</span>
                      <ProgressBar value={pct} max={100} size="sm" className="flex-1" />
                      <span className="text-xs font-semibold text-primary w-8 text-right">{pct}%</span>
                    </div>
                  ))}
                </div>
              )}

              {program.keterangan && (
                <p className="text-xs text-text-secondary mt-2">{program.keterangan}</p>
              )}
            </Card>
          )
        })
      )}
    </div>
  )
}
